import { ChatIcon, CodeIcon, CubeIcon, GlobeAltIcon, SearchIcon } from "@heroicons/react/outline";
import { QuestionMarkCircledIcon } from "@radix-ui/react-icons";
import {
  ArrowUpDownIcon,
  BracesIcon,
  BrainIcon,
  CalculatorIcon,
  CheckCircleIcon,
  CompassIcon,
  DatabaseIcon,
  FileOutputIcon,
  FileTextIcon,
  GitMergeIcon,
  HashIcon,
  LinkIcon,
  ListTree,
  MergeIcon,
  MessagesSquare,
  RefreshCwIcon,
  RegexIcon,
  RouteIcon,
  RulerIcon,
  ScaleIcon,
  ScissorsIcon,
  ServerCog,
  UserIcon,
  Workflow,
} from "lucide-react";

import { DatasetRead } from "./datasets";
import { Template, Metadata } from "./prompt-blueprint";

import { ScoreCard } from "@/components/Evaluate/Reports/ScoreMatrix/types";
import { LanguageType } from "@/components/CodeEditor/types";
import { CellStatus } from "@/components/Evaluate/TableComponents/RowCell/Cells/primitives";

// Shared engine configuration used across prompt template column types
export interface EngineConfiguration {
  provider: string | null;
  provider_base_url_name: string | null;
  model: string;
  model_config_id?: number | null;
  provider_id: number | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parameters: Record<string, any>;
  api_type?: string | null;
}

export interface CodeExecutionConfiguration {
  code: string;
  language: LanguageType;
}

export interface Report {
  created_at: string;
  dataset_id: number;
  id: number;
  folder_id?: number | null;
  dataset_group_name: string;
  dataset_group_id: number;
  is_blueprint: boolean;
  name: string;
  test_dataset_id: number | null;
  updated_at: string;
  user_id: number;
  workspace_id: number;
  author?: string | null;
  parent_report_id: number | null;
  parent_report: Report;
  comment: string | null;
  score: null | ReportScore;
  score_calculation_error?: string;
  children_report_count: number;
  score_configuration?: {
    code: string;
    code_language: LanguageType;
  } | null;
  score_matrix?: ScoreCard[];
  tags?: string[];
  deleted: boolean;
  status?: ReportStatus;
  prompt_version_number: number | null;
  dataset: DatasetRead;
  prompt_registry_id: number | null;
  columns: ReportColumn[]; // Columns are not included by default, but some endpoints can include them
}

export interface ReportStatus {
  COMPLETED: number;
  FAILED: number;
  QUEUED: number;
  RUNNING: number;
}

export type ReportScore =
  | {
      type: "number";
      value: number;
      num_skipped?: number;
    }
  | {
      type: "boolean";
      value: number; // % of true
      num_skipped?: number;
    };

export enum ColumnType {
  DATASET = "DATASET",
  PROMPT_TEMPLATE = "PROMPT_TEMPLATE",
  CONVERSATION_SIMULATOR = "CONVERSATION_SIMULATOR",
  ENDPOINT = "ENDPOINT",
  MCP = "MCP",
  HUMAN = "HUMAN",
  COMPARE = "COMPARE",
  COALESCE = "COALESCE",
  CONDITION = "CONDITION",
  ABSOLUTE_NUMERIC_DISTANCE = "ABSOLUTE_NUMERIC_DISTANCE",
  REGEX = "REGEX",
  JSON_PATH = "JSON_PATH",
  PARSE_VALUE = "PARSE_VALUE",
  CONTAINS = "CONTAINS",
  LLM_ASSERTION = "LLM_ASSERTION",
  AI_DATA_EXTRACTION = "AI_DATA_EXTRACTION",
  VARIABLE = "VARIABLE",
  ASSERT_VALID = "ASSERT_VALID",
  COSINE_SIMILARITY = "COSINE_SIMILARITY",
  COUNT = "COUNT",
  MATH_OPERATOR = "MATH_OPERATOR",
  XML_PATH = "XML_PATH",
  REGEX_EXTRACTION = "REGEX_EXTRACTION",
  COMBINE_COLUMNS = "COMBINE_COLUMNS",
  MIN_MAX = "MIN_MAX",
  CODE_EXECUTION = "CODE_EXECUTION",
  WORKFLOW = "WORKFLOW",
  WHILE_LOOP = "WHILE_LOOP",
  FOR_LOOP = "FOR_LOOP",
  APPLY_DIFF = "APPLY_DIFF",
  TRAJECTORY = "TRAJECTORY",
  PENDING = "PENDING",
}

export const ColumnTypeIcon: Record<ColumnType, React.ComponentType> = {
  [ColumnType.DATASET]: DatabaseIcon,
  [ColumnType.PROMPT_TEMPLATE]: ChatIcon,
  [ColumnType.CONVERSATION_SIMULATOR]: MessagesSquare,
  [ColumnType.ENDPOINT]: GlobeAltIcon,
  [ColumnType.MCP]: ServerCog,
  [ColumnType.HUMAN]: UserIcon,
  [ColumnType.COMPARE]: ScaleIcon,
  [ColumnType.COALESCE]: MergeIcon,
  [ColumnType.CONDITION]: CheckCircleIcon,
  [ColumnType.ABSOLUTE_NUMERIC_DISTANCE]: RulerIcon,
  [ColumnType.REGEX]: RegexIcon,
  [ColumnType.JSON_PATH]: BracesIcon,
  [ColumnType.PARSE_VALUE]: FileTextIcon,
  [ColumnType.CONTAINS]: SearchIcon,
  [ColumnType.LLM_ASSERTION]: BrainIcon,
  [ColumnType.AI_DATA_EXTRACTION]: FileOutputIcon,
  [ColumnType.VARIABLE]: CubeIcon,
  [ColumnType.ASSERT_VALID]: CheckCircleIcon,
  [ColumnType.COSINE_SIMILARITY]: CompassIcon,
  [ColumnType.COUNT]: HashIcon,
  [ColumnType.MATH_OPERATOR]: CalculatorIcon,
  [ColumnType.XML_PATH]: ListTree,
  [ColumnType.REGEX_EXTRACTION]: ScissorsIcon,
  [ColumnType.COMBINE_COLUMNS]: LinkIcon,
  [ColumnType.MIN_MAX]: ArrowUpDownIcon,
  [ColumnType.CODE_EXECUTION]: CodeIcon,
  [ColumnType.WORKFLOW]: Workflow,
  [ColumnType.WHILE_LOOP]: RefreshCwIcon,
  [ColumnType.FOR_LOOP]: RefreshCwIcon,
  [ColumnType.APPLY_DIFF]: GitMergeIcon,
  [ColumnType.TRAJECTORY]: RouteIcon,
  [ColumnType.PENDING]: QuestionMarkCircledIcon,
};

export interface ReportColumn {
  column_type: ColumnType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  configuration: any;
  id?: number; // when we are creating a new report column, we don't have an ID yet
  name: string;
  position: number;
  report_id: number;
  errors?: {
    invalid_columns: string[];
    message: string;
    type: string;
  }[];
  is_part_of_score: boolean;
  score: ReportScore | null;
}

export interface PromptTemplateReference {
  id?: number;
  name: string;
  version_number?: number | null; // null means latest
  label?: string;
}

export interface PromptTemplateConfiguration {
  template?: PromptTemplateReference; // Registry reference (used when mode is "registry")
  inline_template?: InlineTemplate; // Inline content (used when mode is "inline")
  engine?: EngineConfiguration; // optional, if not provided, pull from prompt registry/inline metadata at runtime
  verbose?: boolean;
  return_template_only?: boolean;
  hydrate_input_variables?: boolean;
  prompt_template_variable_mappings: Record<string, string>;
  chat_history_source?: string; // Column source for injecting chat history messages
  mock_tool_responses?: boolean; // Answer every tool call with an AI-generated mock instead of running tool code
  mock_scenario?: string | null; // Optional scenario steering the mock tool responses
  mock_stop_tools?: string | null; // Comma-separated tool names that halt the mock loop when called
}

// Inline prompt template stored directly in configuration (temporary/ephemeral)
export interface InlineTemplate {
  prompt_template: Template;
  metadata?: Metadata | null;
  provider_base_url_name?: string | null;
  provider_id?: number | null;
  // Optional: track which prompt this was derived from
  source_prompt_name?: string | null;
  source_prompt_version?: number | null;
}

export type ConversationSimulatorConfiguration = {
  // Uses separate template and inline_template fields (same as PromptTemplateConfiguration)
  template?: {
    name: string;
    version_number?: number | null; // null means latest
    label?: string;
  };
  inline_template?: InlineTemplate;
  engine?: EngineConfiguration;
  prompt_template_variable_mappings: Record<string, string>;
  is_user_first: boolean;
  conversation_samples: string[];
  max_turns?: number;
  conversation_completed_prompt?: string;
  conversation_completed_prompt_source?: string;
} & (
  | {
      user_persona: string;
      user_persona_source?: never;
    }
  | {
      user_persona?: never;
      user_persona_source: string;
    }
);

export interface EndpointConfiguration {
  url: string;
  headers?: Record<string, string>;
}

export interface MCPServerConfiguration {
  server_url: string;
  headers: Record<string, string>;
}

export interface MCPConfiguration {
  server_configuration: MCPServerConfiguration;
  function_name: string;
  input_data: { path: string; source: string }[];
}

export interface CompareConfiguration {
  sources: [string] | [string, string];
  target?: unknown;
  comparison_type?: // optional for backward compatability, default to STRING
  | {
        type: "STRING";
      }
    | {
        type: "JSON";
        json_path: string | null; // if null, compare the entire JSON object
      }
    | {
        type: "NUMBER";
        operator?: "EQ" | "NE" | "GT" | "GTE" | "LT" | "LTE";
      };
}

export interface AbsoluteNumericDistanceConfiguration {
  sources: [string, string];
}

export interface RegexConfiguration {
  regex_pattern: string;
  source: string;
}

export interface JsonPathConfiguration {
  source: string;
  json_path: string;
  return_first_match: boolean;
}

export interface ParseValueConfiguration {
  type: "string" | "number" | "boolean" | "object";
  source: string;
}

export interface ContainsConfiguration {
  source: string;
  value?: string;
  value_source?: string;
}

export interface LLMAssertionConfiguration {
  prompt?: string;
  source: string;
  prompt_source?: string;
  variable_mappings?: Record<string, string>;
}

export interface AIDataExtractionConfiguration {
  source: string;
  query: string;
}

interface StringValue {
  type: "string";
  value: string;
}

interface JsonValue {
  type: "json";
  value: object;
}

export type Value = StringValue | JsonValue;

export interface VariableConfiguration {
  value: Value;
}

export interface AssertValidConfiguration {
  source: string;
  type: "number" | "object" | "sql";
}

export interface CosineSimilarityConfiguration {
  sources: [string, string];
}

export interface CountConfiguration {
  source: string;
  type: "chars" | "words" | "sentences" | "paragraphs";
  min_count?: number | null;
  max_count?: number | null;
}

export interface TrajectoryConfiguration {
  trace_source: string;
  accepted_scenarios?: string[][];
  expected_source?: string;
  mode: "strict" | "non_strict";
}

export interface MathOperatorConfiguration {
  operator: "lt" | "le" | "gt" | "ge";
  value: number | null;
  sources: [string, string | null];
}

export interface ConditionValueConfig {
  type?: "source" | "static_value";
  source?: {
    name: string;
    json_path?: string | null;
  };
  static_value?: string;
}

export interface ConditionCondition {
  position: number;
  operator: "=" | "!=" | "<" | ">" | "<=" | ">=" | "in" | "not_in" | "is_null" | "is_not_null";
  left_config: ConditionValueConfig;
  right_config: ConditionValueConfig;
}

export interface ConditionConfiguration {
  is_and: boolean;
  conditionals: ConditionCondition[];
  output_on_fail?: "stop_execution";
}

export interface XmlPathConfiguration {
  source: string;
  xml_path: string;
  type: "find" | "findall";
  return_text?: boolean;
}

export interface RegexExtractionConfiguration {
  regex_pattern: string;
  source: string;
}

export interface CombineColumnsConfiguration {
  sources: string[];
}

export const minMaxTypes = ["min", "max"] as const;

export type MinMaxType = (typeof minMaxTypes)[number];

export interface MinMaxConfiguration {
  json_path?: string;
  type: MinMaxType;
  source: string;
}

export interface WorkflowConfiguration {
  name: string;
  workflow_id?: number;
  workflow_label_name?: string;
  workflow_version_number?: number;
  metadata?: Record<string, string>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  input_variables?: Record<string, any>;
  return_all_outputs?: boolean;
  verbose?: boolean;
}

export interface CoalesceConfiguration {
  sources: string[];
}

export interface ApplyDiffConfiguration {
  sources?: [string, string];
  verbose?: boolean;
}

export type HumanConfiguration =
  | {
      data_type: "number";
      ui_element: SliderUIElement | RatingUIElement | TextboxUIElement;
    }
  | {
      data_type: "string";
      ui_element: ChoiceUIElement | TextboxUIElement;
    };

export interface SliderUIElement {
  component: "slider";
  min: number;
  max: number;
}

export interface RatingUIElement {
  component: "rating";
  max: number;
}

export interface ChoiceUIElement {
  component: "choice";
  options: string[];
}

export interface TextboxUIElement {
  component: "text";
}

interface ToolCall {
  id: string;
  function: {
    arguments: string;
    name: string;
  };
  type: string;
}

export interface ReportCell {
  id: number;
  report_column_id: number;
  status: CellStatus;
  dataset_row_id: number; // Row ID
  value: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any;
  };
  display_value:
    | {
        type: "string";
        value: string;
      }
    | {
        type: "number";
        value: number;
      }
    | {
        type: "function";
        value: {
          name: string;
          arguments: string;
        };
      }
    | {
        type: "tool_calls";
        value: ToolCall[];
      }
    | {
        type: "object";
        value: object;
      }
    | {
        type: "output_media";
        value: { url?: string; media_type?: string; type?: string; storage_key?: string };
      }
    | {
        type: "unknown";
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value: any;
      }
    | {
        type: "boolean";
        value: boolean;
      };
  error_message: string | null;
  updated_at: Date;
}

export type ReportColumnStats = {
  report_cell_id: number;
  request_latency: number;
  request_price: number;
  request_input_tokens: number;
  request_output_tokens: number;
}[];
